import get from 'lodash.get';
import { all, call, delay, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import { metricsApi } from '../../api';

import {
  DEFAULT_ASSET,
  DEFAULT_COLOR,
  DEFAULT_METRIC_CODE,
  DEFAULT_MOVING_MATH,
  DEFAULT_SCALE,
  RESOLUTION_24H,
} from '../../constants/app';
import {
  DEFAULT_TRACES_ASSETS,
  DEFAULT_TRACES_CODES,
  DEFAULT_TRACES_COLORS,
  DEFAULT_TRACES_NUMBER,
  MAX_TRACES_NUMBER,
} from '../../constants/compare';

import * as p from '../../constants/params';

import * as routerActions from '../router/actions';
import * as routerSelectors from '../router/selectors';
import { getMetricConfig } from '../utils/selectors';

import * as actions from './actions';
import * as selectors from './selectors';

const EMPTY_VALUE = '';

const getFallbackAsset = (idx) => get(DEFAULT_TRACES_ASSETS, idx, DEFAULT_ASSET);
const getFallbackCode = (idx) => get(DEFAULT_TRACES_CODES, idx, DEFAULT_METRIC_CODE);
const getFallbackColor = (idx) => get(DEFAULT_TRACES_COLORS, idx, DEFAULT_COLOR);

const updateTrace = (idx, value) => (item, index) => {
  if (index === idx) {
    return value;
  }

  return item;
};

const limitTraces = (arr) => arr.filter((_, idx) => idx < MAX_TRACES_NUMBER);

function* generateOptionalParams(metricsCodes) {
  const currencies = [];
  const exchanges = [];
  const miners = [];

  const c = yield select(selectors.selectComputedCurrencies);
  const e = yield select(selectors.selectValidatedExchanges);
  const mi = yield select(selectors.selectComputedMiners);

  metricsCodes.forEach((code, idx) => {
    const { requiredParams } = getMetricConfig(code);
    const currency = requiredParams.includes(p.CURRENCY) ? get(c, idx) : EMPTY_VALUE;
    const exchange = requiredParams.includes(p.EXCHANGE) ? get(e, idx) : EMPTY_VALUE;
    const miner = requiredParams.includes(p.MINER) ? get(mi, idx) : EMPTY_VALUE;

    currencies.push(currency);
    exchanges.push(exchange);
    miners.push(miner);
  });

  return { currencies, exchanges, miners };
}

function* retrieveParams() {
  // required
  const assets = yield select(selectors.selectComputedMetricsAssets);
  const metricsCodes = yield select(selectors.selectComputedMetricsCodes);
  const resolutions = yield select(selectors.selectValidatedResolutions);
  // optional
  const { currencies, exchanges, miners } = yield generateOptionalParams(metricsCodes);
  const movingAverages = yield select(selectors.selectComputedMovingAverages);
  const movingMedians = yield select(selectors.selectComputedMovingMedians);
  const scales = yield select(selectors.selectComputedMetricsScales);

  const params = {
    [p.ASSET]: assets,
    [p.CURRENCY]: currencies,
    [p.EXCHANGE]: exchanges,
    [p.METRIC]: metricsCodes,
    [p.MINER]: miners,
    [p.MOVING_AVERAGE]: movingAverages,
    [p.MOVING_MEDIAN]: movingMedians,
    [p.RESOLUTION]: resolutions,
    [p.SCALE_METRIC]: scales,
  };

  return {
    assets,
    currencies,
    exchanges,
    metricsCodes,
    miners,
    movingAverages,
    movingMedians,
    params,
    resolutions,
    scales,
  };
}

function* retrieveParamsFromUrl() {
  // required
  const assets = yield select(routerSelectors.selectAssets);
  const metricsCodes = yield select(routerSelectors.selectMetricsCodes);
  const resolutions = yield select(routerSelectors.selectResolutions);
  // optional
  const currencies = yield select(routerSelectors.selectCurrencies);
  const exchanges = yield select(routerSelectors.selectExchanges);
  const miners = yield select(routerSelectors.selectMiners);
  const movingAverages = yield select(routerSelectors.selectMovingAverages);
  const movingMedians = yield select(routerSelectors.selectMovingMedians);
  const scales = yield select(routerSelectors.selectMetricsScales);

  return {
    assets,
    currencies,
    exchanges,
    metricsCodes,
    miners,
    movingAverages,
    movingMedians,
    resolutions,
    scales,
  };
}

function* getTraceData({ asset, metricCode, idx, path, exchange, currency, miner, resolution, ...rest }) {
  // ignore if user is not authorised
  const lockedMetrics = yield select(selectors.selectMetricsLocked);
  const assetsAvailable = yield select(selectors.selectAssetsAvailable);

  const isMetricLocked = lockedMetrics[idx];
  const isAssetAvailable = assetsAvailable[idx];

  if (isMetricLocked || !isAssetAvailable) {
    return;
  }

  try {
    yield put({
      type: actions.GET_TRACE_PENDING,
      payload: {
        asset,
        idx,
        metricCode,
      },
    });

    const c = currency || undefined;
    const e = exchange || undefined;
    const mi = miner || undefined;

    const data = yield call(metricsApi.getMetric, path, {
      asset,
      currency: c,
      exchange: e,
      miner: mi,
      resolution,
      ...rest,
    });

    yield put({
      type: actions.GET_TRACE_FULFILLED,
      payload: {
        asset,
        idx,
        data,
      },
    });
  } catch (e) {
    yield put({
      type: actions.GET_TRACE_REJECTED,
      payload: {
        idx,
        e,
      },
    });
  }
}

function* getDependencyData({ metricCode, idx, path, dependency }) {
  // ignore if user is not authorised
  const lockedMetrics = yield select(selectors.selectMetricsLocked);
  const assetsAvailable = yield select(selectors.selectAssetsAvailable);

  const isMetricLocked = lockedMetrics[idx];
  const isAssetAvailable = assetsAvailable[idx];

  if (isMetricLocked || !isAssetAvailable) {
    return;
  }

  try {
    yield put({
      type: actions.GET_DEPENDENCY_PENDING,
      payload: {
        idx,
        metricCode,
      },
    });

    const data = yield call(metricsApi.getMetricDependency, path, dependency);

    yield put({
      type: actions.GET_DEPENDENCY_FULFILLED,
      payload: {
        idx,
        data,
      },
    });
  } catch (e) {
    yield put({
      type: actions.GET_DEPENDENCY_REJECTED,
      payload: {
        idx,
        e,
      },
    });
  }
}

function* getInitialTraces() {
  const tracesLen = yield select(selectors.selectTracesLen);

  if (tracesLen !== 0) {
    const { params } = yield call(retrieveParams);
    yield put(routerActions.updateQueryString(params));

    return;
  }

  const {
    assets,
    metricsCodes,
    movingAverages,
    movingMedians,
    scales,
    exchanges,
    miners,
    currencies,
    resolutions,
  } = yield call(retrieveParamsFromUrl);

  const a = limitTraces(assets);
  const m = limitTraces(metricsCodes);
  const len = Math.max(a.length, m.length);
  const tracesIdx = len ? [...Array(len)] : [...Array(DEFAULT_TRACES_NUMBER)];

  const puts = tracesIdx.map((_, idx) => {
    // required
    const asset = get(a, idx, getFallbackAsset(idx));
    const code = get(m, idx, getFallbackCode(idx));
    const resolution = get(resolutions, idx, RESOLUTION_24H);
    // optional
    const currency = get(currencies, idx);
    const exchange = get(exchanges, idx);
    const miner = get(miners, idx);
    const movingAverage = get(movingAverages, idx, DEFAULT_MOVING_MATH);
    const movingMedian = get(movingMedians, idx, DEFAULT_MOVING_MATH);
    const scale = get(scales, idx, DEFAULT_SCALE);
    const color = getFallbackColor(idx);

    return put(
      actions.addTrace(asset, code, {
        currency,
        exchange,
        miner,
        movingAverage,
        movingMedian,
        scale,
        color,
        resolution,
      }),
    );
  });

  yield all(puts);
}

function* reloadTraces() {
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  const a = limitTraces(assets);
  const m = limitTraces(metricsCodes);
  const len = Math.max(a.length, m.length);
  const tracesIdx = len ? [...Array(len)] : [...Array(DEFAULT_TRACES_NUMBER)];

  const calls = tracesIdx.map((_, idx) => {
    // required
    const asset = get(a, idx, getFallbackAsset(idx));
    const metricCode = get(m, idx, getFallbackCode(idx));
    const resolution = get(resolutions, idx);
    const since = selectors.selectMetricsStart(resolution);

    // optional
    const currency = get(currencies, idx);
    const exchange = get(exchanges, idx);
    const miner = get(miners, idx);

    const { path } = getMetricConfig(metricCode);

    return call(getTraceData, {
      asset,
      resolution,
      currency,
      exchange,
      idx,
      metricCode,
      miner,
      path,
      since,
    });
  });

  yield all(calls);
}

function* changeAssetTrigger({ payload }) {
  const { asset, idx } = payload;
  const { assets } = yield call(retrieveParams);

  // update params
  const a = assets.map(updateTrace(idx, asset));

  yield put(
    routerActions.updateQueryString({
      a,
    }),
  );
}

function* changeAsset({ payload }) {
  const { asset, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const currency = currencies[idx];
  const exchange = exchanges[idx];
  const metricCode = metricsCodes[idx];
  const resolution = resolutions[idx];
  const since = selectors.selectMetricsStart(resolution);
  const { path } = getMetricConfig(metricCode);

  // reset exchange
  yield put(actions.resetExchange(idx));
  // reset miner
  yield put(actions.resetMiner(idx));

  // update params
  const a = assets.map(updateTrace(idx, asset));
  const mi = miners.map(updateTrace(idx, EMPTY_VALUE));

  yield put(
    routerActions.updateQueryString({
      a,
      miner: mi,
    }),
  );

  yield delay(1);

  yield call(getTraceData, {
    asset,
    currency,
    resolution,
    exchange,
    idx,
    metricCode,
    miner: mi[idx],
    path,
    since,
  });
}

function* changeMetric({ payload }) {
  const { metricCode, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const { path, dependency } = getMetricConfig(metricCode);
  const asset = assets[idx];
  const resolution = resolutions[idx];
  const currency = currencies[idx];
  const exchange = exchanges[idx];
  const miner = miners[idx];
  const since = selectors.selectMetricsStart(resolution);

  // udpate params
  const m = metricsCodes.map(updateTrace(idx, metricCode));

  yield put(
    routerActions.updateQueryString({
      m,
    }),
  );

  const calls = [
    call(getTraceData, {
      asset,
      resolution,
      currency,
      exchange,
      idx,
      metricCode,
      miner,
      path,
      since,
    }),
  ];

  if (dependency) {
    calls.push(call(getDependencyData, { idx, metricCode, path, dependency }));
  }

  yield all(calls);
}

function* changeCurrency({ payload }) {
  const { value, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const metricCode = metricsCodes[idx];
  const { path } = getMetricConfig(metricCode);
  const resolution = resolutions[idx];
  const asset = assets[idx];
  const exchange = exchanges[idx];
  const miner = miners[idx];
  const since = selectors.selectMetricsStart(resolution);

  // update params
  const c = currencies.map(updateTrace(idx, value));

  yield put(
    routerActions.updateQueryString({
      c,
    }),
  );

  yield call(getTraceData, {
    asset,
    resolution,
    currency: c[idx],
    exchange,
    idx,
    metricCode,
    miner,
    path,
    since,
  });
}

function* changeExchange({ payload }) {
  const { value, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const metricCode = metricsCodes[idx];
  const { path } = getMetricConfig(metricCode);
  const resolution = resolutions[idx];
  const asset = assets[idx];
  const currency = currencies[idx];
  const miner = miners[idx];
  const since = selectors.selectMetricsStart(resolution);

  // update params
  const e = exchanges.map(updateTrace(idx, value));

  yield put(
    routerActions.updateQueryString({
      e,
    }),
  );

  yield call(getTraceData, {
    asset,
    resolution,
    currency,
    exchange: e[idx],
    idx,
    metricCode,
    miner,
    path,
    since,
  });
}

function* changeMiner({ payload }) {
  const { value, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const metricCode = metricsCodes[idx];
  const { path } = getMetricConfig(metricCode);
  const asset = assets[idx];
  const resolution = resolutions[idx];
  const currency = currencies[idx];
  const exchange = exchanges[idx];
  const since = selectors.selectMetricsStart(resolution);

  // update params
  const mi = miners.map(updateTrace(idx, value));

  yield put(
    routerActions.updateQueryString({
      miner: mi,
    }),
  );

  yield call(getTraceData, {
    asset,
    resolution,
    currency,
    exchange,
    idx,
    metricCode,
    miner: mi[idx],
    path,
    since,
  });
}

function* changeResolution({ payload }) {
  const { value, idx } = payload;
  const { assets, currencies, exchanges, metricsCodes, miners, resolutions } = yield call(retrieveParams);

  // get current metric
  const metricCode = metricsCodes[idx];
  const { path } = getMetricConfig(metricCode);
  const asset = assets[idx];
  const currency = currencies[idx];
  const exchange = exchanges[idx];
  const miner = miners[idx];
  const since = selectors.selectMetricsStart(value);

  // update params
  const r = resolutions.map(updateTrace(idx, value));

  yield put(
    routerActions.updateQueryString({
      [p.RESOLUTION]: r,
    }),
  );

  // reset moving average
  yield put(actions.changeMovingAverage(DEFAULT_MOVING_MATH, idx));
  yield put(actions.changeMovingMedian(DEFAULT_MOVING_MATH, idx));

  yield call(getTraceData, {
    asset,
    currency,
    exchange,
    idx,
    metricCode,
    miner,
    path,
    resolution: r[idx],
    since,
  });
}

function* changeScale({ payload }) {
  const { value, idx } = payload;

  // get current values
  const scales = yield select(selectors.selectComputedMetricsScales);
  const scl = [...scales].map(updateTrace(idx, value));

  // update params
  yield put(
    routerActions.updateQueryString({
      [p.SCALE_METRIC]: scl,
    }),
  );
}

function* changeMovingAverage({ payload }) {
  const { value, idx } = payload;

  // get current values
  const mAverages = yield select(selectors.selectComputedMovingAverages);
  const mMedians = yield select(selectors.selectComputedMovingMedians);
  const mAverage = [...mAverages].map(updateTrace(idx, value));
  const mMedian = [...mMedians].map(updateTrace(idx, 0));

  // update params
  yield put(
    routerActions.updateQueryString({
      [p.MOVING_AVERAGE]: mAverage,
      [p.MOVING_MEDIAN]: mMedian,
    }),
  );
}

function* changeMovingMedian({ payload }) {
  const { value, idx } = payload;

  // get current values
  const mMedians = yield select(selectors.selectComputedMovingMedians);
  const mAverages = yield select(selectors.selectComputedMovingAverages);
  const mMedian = [...mMedians].map(updateTrace(idx, value));
  const mAverage = [...mAverages].map(updateTrace(idx, 0));

  // update params
  yield put(
    routerActions.updateQueryString({
      [p.MOVING_MEDIAN]: mMedian,
      [p.MOVING_AVERAGE]: mAverage,
    }),
  );
}

function* addTrace({ payload }) {
  const { asset, metricCode } = payload;
  const len = yield select(selectors.selectTracesLen);
  const idx = len - 1;

  const metric = getMetricConfig(metricCode);
  const { path, dependency } = metric;

  const { currencies, exchanges, miners, resolutions, params } = yield call(retrieveParams);
  const currency = currencies[idx];
  const exchange = exchanges[idx];
  const miner = miners[idx];
  const resolution = resolutions[idx];
  const since = selectors.selectMetricsStart(resolution);

  // update params
  yield put(routerActions.updateQueryString(params));

  const calls = [
    call(getTraceData, {
      asset,
      currency,
      exchange,
      idx,
      metricCode,
      miner,
      path,
      resolution,
      since,
    }),
  ];

  if (dependency) {
    calls.push(call(getDependencyData, { idx, metricCode, path, dependency }));
  }

  yield all(calls);
}

function* deleteTrace() {
  const { params } = yield call(retrieveParams);

  // update params
  yield put(routerActions.updateQueryString(params));
}

export function* watchCompareGetInitialTraces() {
  yield takeLatest(actions.GET_INITIAL_TRACES, getInitialTraces);
}

export function* watchCompareReloadTraces() {
  yield takeLatest(actions.RELOAD_TRACES, reloadTraces);
}

export function* watchCompareChangeAsset() {
  yield takeLatest(actions.CHANGE_ASSET, changeAsset);
}

export function* watchCompareChangeAssetTrigger() {
  yield takeLatest(actions.CHANGE_ASSET_TRIGGER, changeAssetTrigger);
}

export function* watchCompareChangeMetric() {
  yield takeLatest(actions.CHANGE_METRIC, changeMetric);
}

export function* watchCompareChangeScale() {
  yield takeLatest(actions.CHANGE_SCALE, changeScale);
}

export function* watchCompareChangeMovingAverage() {
  yield takeLatest(actions.CHANGE_MOVING_AVERAGE, changeMovingAverage);
}

export function* watchCompareChangeMovingMedian() {
  yield takeLatest(actions.CHANGE_MOVING_MEDIAN, changeMovingMedian);
}

export function* watchCompareChangeCurrency() {
  yield takeLatest(actions.CHANGE_CURRENCY, changeCurrency);
}

export function* watchCompareChangeExchange() {
  yield takeLatest(actions.CHANGE_EXCHANGE, changeExchange);
}

export function* watchCompareChangeMiner() {
  yield takeLatest(actions.CHANGE_MINER, changeMiner);
}

export function* watchCompareChangeResolution() {
  yield takeLatest(actions.CHANGE_RESOLUTION, changeResolution);
}

export function* watchCompareAddTrace() {
  yield takeEvery(actions.ADD_TRACE, addTrace);
}

export function* watchCompareDeleteTrace() {
  yield takeLatest(actions.DELETE_TRACE, deleteTrace);
}
